import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { tokensAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Coins, 
  TrendingUp, 
  Calendar,
  ArrowUpCircle,
  ArrowDownCircle,
  Trophy,
  Sparkles
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function TokensSection() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsResponse, transactionsResponse] = await Promise.all([
        tokensAPI.getStats(),
        tokensAPI.getTransactions(20),
      ]);
      setStats(statsResponse.data);
      setTransactions(transactionsResponse.data);
    } catch (error) {
      console.error('Failed to load tokens data:', error);
      toast.error('Erro ao carregar dados de tokens');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  const displayedTransactions = showAllTransactions 
    ? transactions 
    : transactions.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Coins className="h-6 w-6 text-white" />
              </div>
              {stats?.user_type === 'FOUNDER' && (
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0">
                  Fundador 2x
                </Badge>
              )}
            </div>
            <div className="text-3xl font-bold text-yellow-600 mb-1">
              {stats?.total_tokens || 0}
            </div>
            <div className="text-sm text-gray-600">Tokens Disponíveis</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {stats?.lifetime_tokens || 0}
            </div>
            <div className="text-sm text-gray-600">Total Acumulado</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Trophy className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-green-600 mb-1">
              {stats?.tasks_completed_today || 0}
            </div>
            <div className="text-sm text-gray-600">Tarefas Hoje</div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Earnings */}
      {stats && stats.tokens_earned_today > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Sparkles className="h-8 w-8 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-green-600">
                  +{stats.tokens_earned_today} tokens ganhos hoje!
                </div>
                <div className="text-sm text-gray-600">
                  Continue completando tarefas para ganhar mais recompensas
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Founder Bonus Info */}
      {stats?.user_type === 'FOUNDER' && stats?.founder_bonus && (
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg">
                <Trophy className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold mb-1">
                  Bônus de Fundador Ativo!
                </div>
                <div className="text-sm text-gray-600">
                  Você está ganhando <strong>2x tokens</strong> em todas as tarefas. 
                  {stats.founder_bonus.extra_tokens > 0 && (
                    <span className="text-yellow-600 font-semibold">
                      {' '}+{stats.founder_bonus.extra_tokens} tokens extras hoje!
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade to Founder CTA */}
      {stats?.user_type !== 'FOUNDER' && (
        <Card className="bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Sparkles className="h-8 w-8 text-yellow-600" />
              <div className="flex-1">
                <div className="text-lg font-bold mb-1">
                  Quer ganhar 2x mais tokens?
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  Membros do Plano Fundador recebem o dobro de recompensas em todas as tarefas!
                </div>
                <Button
                  onClick={() => window.location.href = '/checkout'}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                >
                  Ser Fundador Agora
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Histórico de Transações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Coins className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma transação ainda</p>
              <p className="text-sm mt-2">Complete tarefas para começar a ganhar tokens!</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {displayedTransactions.map((transaction) => {
                  const isEarned = transaction.transaction_type === 'EARNED';
                  
                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          isEarned ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {isEarned ? (
                            <ArrowUpCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <ArrowDownCircle className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        
                        <div>
                          <div className="font-semibold">
                            {transaction.description}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDistanceToNow(new Date(transaction.created_at), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </div>
                        </div>
                      </div>

                      <div className={`text-lg font-bold ${
                        isEarned ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {isEarned ? '+' : '-'}{transaction.amount}
                      </div>
                    </div>
                  );
                })}
              </div>

              {transactions.length > 5 && (
                <div className="mt-4 text-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowAllTransactions(!showAllTransactions)}
                  >
                    {showAllTransactions ? 'Ver Menos' : `Ver Todas (${transactions.length})`}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
